<template>
    <button
        class="mag-button _button"
        ref="el"
        :class="{ inline, primary, gradate, danger, rounded, full, mini, chip }"
        :type="type"
        @click="emit('click', $event)"
    >
        <mg-icon v-if="typeof icon === 'string'" :icon marginEnd="true" />
        <mg-icon
            v-else-if="typeof icon !== 'undefined'"
            v-bind="icon"
            marginEnd="true"
        />
        <span v-if="$slots.default">
            <slot></slot>
        </span>
    </button>
</template>

<script lang="ts" setup>
import { nextTick, onMounted, useTemplateRef } from "vue";

const props = defineProps<{
    type?: "button" | "submit" | "reset";
    primary?: boolean;
    gradate?: boolean;
    rounded?: boolean;
    inline?: boolean;
    to?: string;
    autofocus?: boolean;
    wait?: boolean;
    danger?: boolean;
    full?: boolean;
    mini?: boolean;
    chip?: boolean;
    icon?: string | {};
}>();

const emit = defineEmits<{
    (ev: "click", payload: MouseEvent): void;
}>();

const el = useTemplateRef("el");

onMounted(() => {
    if (props.autofocus) {
        nextTick(() => {
            el.value?.focus();
        });
    }
});
</script>

<style lang="scss" scoped>
@use "@/style/mag-shared";

.mag-button {
    @include mag-shared.mag-surface-interactive;

    display: block;
    min-width: 100px;
    min-height: 35px;
    width: max-content;
    padding: 8px 16px;
    text-align: center;
    font-weight: normal;
    font-size: max(12px, 1em);
    box-shadow: none;
    text-decoration: none;
    background: var(--buttonBg);
    overflow: clip;
    box-sizing: border-box;
    transition: background 0.1s ease;
    margin-right: 0.2rem;
    margin-left: 0.2rem;

    &.full {
        width: 100%;
    }

    &.rounded {
        border-radius: 999px;
    }

    &.primary {
        font-weight: bold;
        color: var(--fgOnAccent);
        background: var(--accent);
    }

    &.gradate {
        font-weight: bold;
        color: var(--fgOnAccent);
        background: linear-gradient(
            90deg,
            var(--buttonGradateA),
            var(--buttonGradateB)
        );

        &:not(:disabled):hover {
            background: linear-gradient(90deg, var(--X8), var(--X8));
        }

        &:not(:disabled):active {
            background: linear-gradient(90deg, var(--X8), var(--X8));
        }
    }

    &.danger {
        color: #eb6f92;

        &.primary {
            color: #e0def4;
            background: #eb6f92;

            &:not(:disabled):hover {
                background: #eb6f92;
            }

            &:not(:disabled):active {
                background: #b4637a;
            }
        }
    }

    &.mini {
        padding: 4px 8px;
        font-size: max(12px, 0.9em);
        border-radius: 100px;
    }

    &.chip {
        padding: 4px 12px;
        font-size: max(12px, 0.9em);
        min-width: unset;
        border-radius: 100px;
    }

    &:disabled {
        opacity: 0.7;
    }

    &:focus-visible {
        outline: auto;
    }

    &.inline {
        display: inline-block;
        width: auto;
        min-width: 100px;
    }
}
</style>
